import { motion } from "framer-motion";
import { Check, X, Eye, Clock, DollarSign, Server, Bot, CheckCircle2, UserCheck } from "lucide-react";
import type { HiveApproval } from "@/types";
import { useExecution } from "@/store/executionStore";
import { RiskBadge, Pill } from "@/components/hive/shared";
import { SampleBadge } from "@/components/ui/SampleBadge";

export function ApprovalQueue() {
  const approvals = useExecution((s) => s.approvals);
  const approve = useExecution((s) => s.approve);
  const reject = useExecution((s) => s.reject);
  const openModal = useExecution((s) => s.openApprovalModal);
  const select = useExecution((s) => s.selectExecution);

  const pending = approvals.filter((a) => a.status === "pending");
  const history = approvals.filter((a) => a.status !== "pending");

  return (
    <div className="h-full overflow-y-auto no-scrollbar p-4">
      <div className="mb-3 flex items-center gap-2">
        <UserCheck size={16} className="text-fuchsia-400" />
        <span className="text-sm font-semibold text-app">Approval Queue</span>
        <Pill color="#a855f7">{pending.length} pending</Pill>
      </div>

      {pending.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-app py-16 text-center">
          <CheckCircle2 size={26} className="text-emerald-500" />
          <div className="mt-2 text-sm font-medium text-app">Queue is clear</div>
          <div className="text-xs text-app-2">No actions are waiting on a human right now.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {pending.map((a, i) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <ApprovalCard
                a={a}
                onApprove={() => approve(a.id)}
                onReject={() => reject(a.id, "")}
                onReview={() => { select(a.executionId); openModal(a.id); }}
              />
            </motion.div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-app-2">Approval history</div>
          <div className="space-y-1">
            {history.map((a) => (
              <div key={a.id} className="flex items-center gap-2 rounded-lg border border-app bg-surface/40 px-3 py-1.5 text-[11px]">
                <span className={`h-1.5 w-1.5 rounded-full ${a.status === "approved" ? "bg-emerald-500" : "bg-rose-500"}`} />
                <span className="font-medium text-app">{a.action}</span>
                <span className="text-app-2">· {a.workflowName}</span>
                <span className="ml-auto capitalize text-app-2">{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ a, onApprove, onReject, onReview }: { a: HiveApproval; onApprove: () => void; onReject: () => void; onReview: () => void }) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-app bg-surface/50 p-3.5 backdrop-blur-xl">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-app">{a.action}</span>
          {a.isSample && <SampleBadge />}
        </div>
        <RiskBadge risk={a.risk} />
      </div>
      <div className="mt-0.5 text-[11px] text-app-2">{a.workflowName}</div>

      <div className="mt-2.5 rounded-lg border border-app bg-surface-2/40 px-2.5 py-2 text-[11px] leading-relaxed text-app-1">
        {a.preview}
      </div>

      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-app-2">
        <span className="flex items-center gap-1"><Bot size={10} /> {a.requester}</span>
        <span className="flex items-center gap-1"><Server size={10} /> <span className="truncate">{a.system}</span></span>
        <span className="flex items-center gap-1"><Clock size={10} /> {a.timeout}</span>
        {a.costImpact > 0 && <span className="flex items-center gap-1 text-amber-500"><DollarSign size={10} /> +${a.costImpact.toFixed(2)}</span>}
      </div>

      <div className="mt-3 flex items-center gap-1.5">
        <button onClick={onApprove} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-emerald-500 py-1.5 text-[11px] font-semibold text-white transition hover:brightness-110"><Check size={12} /> Approve</button>
        <button onClick={onReject} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-rose-500/15 py-1.5 text-[11px] font-semibold text-rose-500 transition hover:bg-rose-500/25"><X size={12} /> Reject</button>
        <button onClick={onReview} className="grid h-7 w-8 place-items-center rounded-lg border border-app bg-surface-2/60 text-app-1 transition hover:border-app-strong" title="Review"><Eye size={13} /></button>
      </div>
    </div>
  );
}
